import React from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import './App.css';
import Navigator from './components/Navigator';
import { setUserSession, isUserLoggedIn, setThemeType } from './helpers/session'

import Login from "./components/views/Login"
import ManageUserView from "./components/views/Users/ManageUserView"
import ManageGateView from "./components/views/Gates/ManageGateView"
import ManageCardView from "./components/views/Cards/ManageCardView"
import { User } from './types/User';
import { createTheme, ThemeProvider, CssBaseline, useScrollTrigger, Box } from '@mui/material';

import { NotifyProvider } from './components/Notify/NotifyProvider'
import { Navigate } from 'react-router-dom';

import { ConfirmProvider } from './components/modals/ConfirmModal';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import HistoryView from './components/views/History/HistoryView';

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
  children?: React.ReactElement;
}

function ElevationScroll(props: any) {
  // Note that you normally won't need to set the window ref as useScrollTrigger
  // will default to window.
  // This is only being set here because the demo is in an iframe.
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window ? props.window() : undefined,
  });

  return React.cloneElement(props.children, {
    elevation: trigger ? 4 : 0,
  });
}

let theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#21A651',
    },
    secondary: {
      main: '#0679FF',
    },
    background: {
      default: '#263340',
      paper: '#1D2833'
    }

  },
  typography: {
    button: {
      fontWeight: 500,
      fontSize: 14,
    },
    h5: {
      fontWeight: 500,
      fontSize: 26,
      letterSpacing: 0.5,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
    },
  },
  mixins: {
    toolbar: {
      minHeight: 48,
    },
  },
});

theme = {
  ...theme,
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#2F3D4C',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          size: 'small',
          textTransform: 'none',
        },
        contained: {
          boxShadow: 'none',
          '&:active': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          marginLeft: theme.spacing(1),
        },
        indicator: {
          height: 3,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
          backgroundColor: theme.palette.common.white,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          margin: '0 16px',
          minWidth: 0,
          padding: 0,
          [theme.breakpoints.up('md')]: {
            padding: 0,
            minWidth: 0,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: theme.spacing(1),
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 4,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgb(255,255,255,0.15)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            color: '#21A651',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: 14,
          fontWeight: theme.typography.fontWeightMedium,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: 'inherit',
          minWidth: 'auto',
          marginRight: theme.spacing(2),
          '& svg': {
            fontSize: 20,
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: 32,
          height: 32,
        },
      },
    },
  },
};

const App = (props: Props) => {

  const drawerWidth = 256;

  //Force render on login/logout
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);

  const onLogin = (user: User, token: string, expiresIn: number) => {
    setUserSession(user, token, expiresIn);
    forceUpdate();
  }

  const onLogout = () => {
    localStorage.clear();
    forceUpdate();
  }

  const handleThemeChange = (type: 'dark' | 'light') => {
    setThemeType(type);
    forceUpdate();
  }

  const PrivateRoute = ({ children }: any) => {
    const auth = isUserLoggedIn();
    return auth ? children : <Navigate to="/login" />;
  }

  const HomeRoute = () => {
    const auth = isUserLoggedIn();
    return auth ? <Navigate to="/gates" /> : <Navigate to="/login" />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <ConfirmProvider>
            <BrowserRouter >
              <NotifyProvider>
                <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
                  <Navigator PaperProps={{ style: { width: drawerWidth } }}
                    sx={{ display: { sm: 'block', xs: 'none' } }} />
                </Box>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: { sm: `calc(100% - ${drawerWidth}px)` } }}>
                  <Box component="main" sx={{ flex: 1 }}>
                    <Routes>
                      <Route path="/login" element={<Login onLogin={onLogin} />} />
                      <Route path="/users" element={<PrivateRoute><ManageUserView /></PrivateRoute>} />
                      <Route path="/cards" element={<PrivateRoute><ManageCardView /></PrivateRoute>} />
                      <Route path="/gates" element={<PrivateRoute><ManageGateView /></PrivateRoute>} />
                      <Route path="/history" element={<PrivateRoute><HistoryView /></PrivateRoute>} />
                      <Route path="/" element={<HomeRoute />} />
                    </Routes>
                  </Box>
                </Box>

              </NotifyProvider>
            </BrowserRouter >
          </ConfirmProvider>
        </LocalizationProvider>
      </Box>
    </ThemeProvider >

  );
}

export default App;
